// @ts-nocheck
import React from "react";
import LegalPageLayout, { BulletList, Section } from "../components/LegalPageLayout";

export default function DisputePolicy() {
  return (
    <LegalPageLayout
      title="Dispute & Reporting Policy"
      subtitle="How to report issues and what happens when you do."
      updated="February 7, 2026"
    >
      <Section title="Overview">
        <p>
          Cardex provides a fair reporting system to keep the trading community safe. This policy 
          outlines how to report issues and what happens when you do.
        </p>
      </Section>

      <Section title="What You Can Report (Phase 1)">
        <BulletList
          items={[
            "Users posting counterfeit or fake cards",
            "Harassment or abusive messages",
            "Scam attempts or suspicious behavior",
            "Inappropriate profiles or content",
            "Safety concerns during meetups"
          ]}
        />
      </Section>

      <Section title="How to Report a User">
        <BulletList
          items={[
            "Navigate to the user's profile",
            "Tap the menu icon (⋮) in the top right",
            'Select "Report User"',
            "Choose the reason for reporting",
            "Provide details and evidence (screenshots recommended)",
            "Submit the report"
          ]}
        />
      </Section>

      <Section title="How to Report a Message">
        <BulletList
          items={[
            "Open the conversation with the issue",
            "Long-press the specific message",
            'Select "Report Message"',
            "Choose the violation type",
            "Submit (the message is automatically flagged for review)"
          ]}
        />
      </Section>

      <Section title="Evidence Requirements">
        <p>
          For reports involving counterfeit cards, scams, or harassment, providing evidence 
          helps our team make faster decisions:
        </p>
        <BulletList
          items={[
            "Screenshots of problematic messages",
            "Photos of counterfeit cards being advertised",
            "Details about what happened during a meetup",
            "Any other relevant information"
          ]}
        />
      </Section>

      <Section title="Review Process">
        <p>
          Our moderation team reviews all reports within <strong>24-48 hours</strong>. We evaluate:
        </p>
        <BulletList
          items={[
            "Severity of the violation",
            "Evidence provided by the reporter",
            "User's history and prior reports",
            "Context and communication patterns"
          ]}
        />
      </Section>

      <Section title="Possible Outcomes">
        <p>Based on the severity and context, reports can result in:</p>
        
        <h3>Warning</h3>
        <p>User receives a warning and their profile is flagged. Used for first-time or minor violations.</p>
        
        <h3>Suspension</h3>
        <p>Account temporarily suspended. Applied for repeated violations or serious misconduct.</p>
        
        <h3>Permanent Ban</h3>
        <p>
          Account permanently banned. Reserved for severe violations, repeat offenses, or threats to 
          community safety.
        </p>
      </Section>

      <Section title="Strike System">
        <p>Reports that result in disciplinary action contribute to a user's strike count:</p>
        <BulletList
          items={[
            "1 Strike: Warning issued, profile marked",
            "2 Strikes: Temporary suspension",
            "3 Strikes: Permanent ban"
          ]}
        />
        <p style={{ 
          background: "linear-gradient(135deg, rgba(255, 183, 0, 0.15), rgba(255, 152, 0, 0.1))",
          borderLeft: "3px solid rgba(255, 183, 0, 0.6)",
          padding: "14px 18px",
          borderRadius: "8px",
          marginTop: "14px",
          color: "rgba(255, 220, 150, 0.95)",
          fontWeight: "500"
        }}>
          Severe violations (threats, illegal activity, repeat counterfeiting) may result in 
          immediate ban regardless of strike count.
        </p>
      </Section>

      <Section title="Counterfeit Policy">
        <p>
          Cardex has a <strong>zero-tolerance policy</strong> for counterfeit cards:
        </p>
        <BulletList
          items={[
            "Listings are immediately removed",
            "User receives 2 strikes or immediate ban",
            "All active trades/bounties from that user are suspended",
            "Serious cases may be reported to law enforcement"
          ]}
        />
      </Section>

      <Section title="Abuse Prevention">
        <p>Filing false reports or abusing the reporting system will result in:</p>
        <BulletList
          items={[
            "Report rejection",
            "Account warning or suspension",
            "Loss of reporting privileges"
          ]}
        />
        <p>
          We track reporting patterns and flag accounts with suspicious or excessive claims.
        </p>
      </Section>

      <Section title="🚧 Phase 2: Transaction Disputes (Coming Soon)">
        <p style={{ 
          background: "linear-gradient(135deg, rgba(124, 243, 255, 0.1), rgba(111, 66, 193, 0.1))",
          padding: "14px 18px",
          borderRadius: "10px",
          border: "1px solid rgba(124, 243, 255, 0.25)",
          color: "rgba(124, 243, 255, 0.95)",
          fontWeight: "500",
          marginBottom: "14px"
        }}>
          When marketplace features launch, additional dispute types will be available:
        </p>
        <BulletList
          items={[
            "Payment and refund disputes",
            "Shipping and delivery issues",
            "Item condition discrepancies",
            "72-hour inspection windows"
          ]}
        />
        <p style={{ 
          background: "rgba(124, 243, 255, 0.1)",
          borderLeft: "3px solid rgba(124, 243, 255, 0.5)",
          padding: "14px 18px",
          borderRadius: "8px",
          marginTop: "14px",
          color: "rgba(124, 243, 255, 0.9)",
          fontWeight: "500"
        }}>
          These features are not yet active. Phase 1 focuses on local, in-person trading only.
        </p>
      </Section>

      <Section title="Contact Support">
        <p>
          If you have questions about reporting, need help with a specific case, or want to check 
          the status of a report, contact us at:
        </p>
        <p><strong>support@cardexhq.com</strong></p>
      </Section>

      <p className="legal-footer-text">
        Our reporting system is designed to keep the Cardex community safe and trustworthy.
      </p>
    </LegalPageLayout>
  );
}
